import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Info, Save } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useFormSuccess } from "@/shared/hooks/useFormSuccess";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, selectClass, textareaClass } from "../components/FormField";
import {
  createHospitalDispute,
  fetchHospitalDisputeInvoiceOptions,
} from "../api/disputeApi";
import { getAccountTypeRoute, getAuthToken, getStoredAccountType } from "../auth";
import { hospitalDisputeInvoiceOptions, invoiceOptions } from "../data";
import type { InvoiceOption } from "../types";

export function CreateDisputePage() {
  const { t } = useTranslation();
  const saveSuccess = useFormSuccess();
  const { showToast } = usePageActions();
  const labels = t.safecheck.disputes;
  const fields = labels.fields;
  const createForm = labels.createForm;

  const [accountType] = useState(() => getStoredAccountType());
  const homeHref = accountType ? getAccountTypeRoute(accountType) : "/dashboard/hospital";

  const [invoiceOptionsList, setInvoiceOptionsList] = useState<InvoiceOption[]>(() =>
    accountType === "hospital" ? hospitalDisputeInvoiceOptions : invoiceOptions,
  );
  const [invoiceId, setInvoiceId] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (accountType !== "hospital" || !getAuthToken()) return;

    let cancelled = false;
    void (async () => {
      try {
        const invoices = await fetchHospitalDisputeInvoiceOptions();
        if (cancelled) return;
        setInvoiceOptionsList(
          invoices.map((row) => ({
            value: String(row.id),
            label: row.label,
          })),
        );
      } catch {
        /* keep mock options */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountType]);

  const invoiceSelectOptions = useMemo(() => invoiceOptionsList, [invoiceOptionsList]);

  const handleSubmit = async () => {
    if (!invoiceId || !description.trim()) {
      showToast({ message: "Facture et description obligatoires.", variant: "warning" });
      return;
    }

    if (!getAuthToken() || accountType !== "hospital") {
      saveSuccess("/dashboard/disputes", t.actions.saved);
      return;
    }

    setSaving(true);
    try {
      await createHospitalDispute({
        invoice_id: Number(invoiceId),
        description: description.trim(),
      });
      saveSuccess("/dashboard/disputes", t.actions.saved);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Création du litige impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref={homeHref}
          items={[
            { label: labels.title, href: "/dashboard/disputes" },
            { label: labels.create },
          ]}
        />
        <Link
          to="/dashboard/disputes"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={16} />
          {createForm.backToList}
        </Link>
      </div>

      <div className="theme-card p-5 md:p-6">
        <div className="mb-6 flex items-center gap-2 text-base font-semibold text-foreground">
          <Info size={16} className="text-primary" />
          {labels.newTitle}
        </div>

        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <FormField label={fields.invoice} required hint={fields.invoiceHint}>
            <select
              className={selectClass}
              value={invoiceId}
              onChange={(event) => setInvoiceId(event.target.value)}
              required
            >
              <option value="">{fields.invoicePlaceholder}</option>
              {invoiceSelectOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label={fields.description} required>
            <textarea
              className={`${textareaClass} min-h-[180px]`}
              placeholder={fields.descriptionPlaceholder}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              required
            />
            <div className="mt-2 text-xs text-muted">
              <p>{fields.descriptionHelpIntro}</p>
              <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
                <li>{fields.descriptionHelpBullets.problem}</li>
                <li>{fields.descriptionHelpBullets.amounts}</li>
                <li>{fields.descriptionHelpBullets.documents}</li>
                <li>{fields.descriptionHelpBullets.other}</li>
              </ul>
            </div>
          </FormField>

          <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary-soft px-4 py-3">
            <Info size={18} className="mt-0.5 shrink-0 text-primary" />
            <p className="text-sm leading-relaxed text-foreground">{createForm.infoBanner}</p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-6">
            <Link
              to="/dashboard/disputes"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <ArrowLeft size={16} />
              {createForm.back}
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
            >
              <Save size={16} />
              {labels.submit}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
